import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Empty, Input, Space, Typography } from 'antd'
import { HomeOutlined, SearchOutlined } from '@ant-design/icons'
import { IntegrationIcons } from '../integration-icons'
import {
  EmployeeRoles,
  InviteButton,
  StatusDot,
  employeeInitials,
  flattenOrganizations,
  inviteExceptionLabel,
  sortActiveFirst,
} from './shared'
import type { CompanyStructureViewProps, Employee, Office, Organization } from './types'

const { Text, Title } = Typography

/**
 * V3 — Панели офисов.
 * Организация = секция-заголовок; офис = отдельная визуальная панель.
 * Неактивность = полоска слева + приглушение, без тега.
 * Поиск по ФИО/логину внутри всей компании.
 */
export function CompanyStructureV3Panels({ structure, onInvite }: CompanyStructureViewProps) {
  const [query, setQuery] = useState('')
  const orgs = useMemo(() => flattenOrganizations(structure), [structure])
  const q = query.trim().toLowerCase()

  return (
    <div>
      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        placeholder="Найти сотрудника по имени или email…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        style={{ marginBottom: 16, maxWidth: 420 }}
      />

      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {orgs.map(org => (
          <OrgPanels key={org.id} org={org} onInvite={onInvite} query={q} />
        ))}
      </Space>
    </div>
  )
}

function OrgPanels({
  org,
  onInvite,
  query,
}: {
  org: Organization
  onInvite: CompanyStructureViewProps['onInvite']
  query: string
}) {
  const offices = sortActiveFirst(org.offices)
  const filteredOffices = offices
    .map(office => ({
      office,
      employees: sortActiveFirst(office.employees).filter(e => {
        if (!query) return true
        const hay = `${e.name} ${e.login}`.toLowerCase()
        return hay.includes(query)
      }),
    }))
    .filter(item => (!query ? true : item.employees.length > 0))

  if (query && filteredOffices.length === 0) return null

  return (
    <section style={{ opacity: org.is_active ? 1 : 0.55 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <StatusDot active={org.is_active} />
        <Title level={5} style={{ margin: 0 }}>
          <Link to={`/new/organization/${org.id}`}>{org.name}</Link>
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {org.offices.length} офис. · {org.offices.reduce((n, o) => n + o.employees.length, 0)}{' '}
          сотр.
        </Text>
      </div>

      {offices.length === 0 ? (
        <Text type="secondary">
          Нет офисов · <Link to={`/office/${org.id}/new`}>добавить</Link>
        </Text>
      ) : filteredOffices.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ничего не найдено" />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {filteredOffices.map(({ office, employees }) => (
            <OfficePanel
              key={office.id}
              org={org}
              office={office}
              employees={employees}
              onInvite={onInvite}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function OfficePanel({
  org,
  office,
  employees,
  onInvite,
}: {
  org: Organization
  office: Office
  employees: Employee[]
  onInvite: CompanyStructureViewProps['onInvite']
}) {
  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 10,
        background: office.is_active ? '#fff' : '#fafafa',
        opacity: office.is_active ? 1 : 0.55,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 160,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
          padding: '10px 12px',
          background: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Space direction="vertical" size={4} style={{ minWidth: 0 }}>
          <Space size={6}>
            <HomeOutlined style={{ color: '#8c8c8c' }} />
            <Link to={`/office/${office.id}`}>
              <Text strong ellipsis>
                {office.name}
              </Text>
            </Link>
          </Space>
          <Space size={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {employees.length} сотр.
            </Text>
            <IntegrationIcons integrations={office.integrations} size={14} />
          </Space>
        </Space>
        {office.is_active && org.is_active && (
          <InviteButton org={org} office={office} onInvite={onInvite} />
        )}
      </div>

      <div style={{ padding: '4px 0', flex: 1 }}>
        {employees.length === 0 ? (
          <div style={{ padding: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Пусто
            </Text>
          </div>
        ) : (
          employees.map(emp => <EmployeeLine key={emp.id} employee={emp} />)
        )}
      </div>
    </div>
  )
}

function EmployeeLine({ employee }: { employee: Employee }) {
  const exception = inviteExceptionLabel(employee)
  const inactive = !employee.is_active && !exception
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '8px 12px',
        borderLeft: `3px solid ${
          exception
            ? employee.invite_status === 'expired'
              ? '#fa8c16'
              : '#faad14'
            : employee.is_active
            ? 'transparent'
            : '#d9d9d9'
        }`,
        opacity: inactive ? 0.45 : 1,
      }}
    >
      <Avatar size={28} style={{ background: '#1677ff', flexShrink: 0 }}>
        {employeeInitials(employee)}
      </Avatar>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Space size={6} wrap>
          <Link to={`/employees_new/${employee.id}`}>
            <Text style={{ fontSize: 13 }}>{employee.name || employee.login}</Text>
          </Link>
          {exception}
          <IntegrationIcons integrations={employee.integrations} size={12} services={['mango']} />
        </Space>
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {employee.login}
          </Text>
        </div>
        <div style={{ marginTop: 2 }}>
          <EmployeeRoles roles={employee.roles} />
        </div>
      </div>
    </div>
  )
}
